import { Observation } from '@medplum/fhirtypes';
import { formatHumanName, MedplumClient } from '@medplum/core';
import { observationWeightToKg, totalWeightLossPercent } from '@bariendo/lib/bmi';
import { getInitialsFromHumanName } from '@bariendo/lib/human-name';
import { Track, TRACK_CODESYSTEM } from '@bariendo/lib/constants';
import { formatDate } from '@/services/date';
import { kgToLbs } from '@/services/patient';
import { getBariatricProcedureStageLabel, getEmployer } from '@/services/patient-fields';
import { parse } from 'graphql';
import { processGraphQLQuery } from '@/hooks/useLiveGraphQL';
import { CARRUM_PATIENT_LIST_QUERY } from './graphql';
import { PatientListQueryResultItem } from './types';

/**
 * Formats a body-weight Observation as whole pounds (e.g. "295 lb").
 * Uses the same kg normalization/conversion helpers as the patient list's TWL column
 * (see PatientListPage/services.tsx#formatPostProcedureTwlDisplay).
 */
export function formatWeightLb(observation?: Observation): string {
  const kg = observationWeightToKg(observation);
  if (kg === undefined) {
    return '';
  }
  return `${kgToLbs(kg)} lb`;
}

/**
 * Formats total weight change between a first and last weight Observation.
 * A loss renders as e.g. "−45 lb · 15.3%" (U+2212 minus); a gain renders as
 * e.g. "+45 lb · 18.0%" (leading plus, non-negative percent).
 * Returns '' when either observation is missing, unparseable, or the weight is unchanged.
 */
export function getWeightLossDisplay(first?: Observation, last?: Observation): string {
  const startKg = observationWeightToKg(first);
  const lastKg = observationWeightToKg(last);
  if (startKg === undefined || lastKg === undefined) {
    return '';
  }

  const startLb = kgToLbs(startKg);
  const lastLb = kgToLbs(lastKg);
  const deltaLb = startLb - lastLb;
  if (Math.round(deltaLb) === 0 || startLb <= 0) {
    return '';
  }

  if (deltaLb > 0) {
    const pct = totalWeightLossPercent(deltaLb, startLb);
    return `−${Math.round(deltaLb)} lb · ${pct.toFixed(1)}%`;
  }

  const gainLb = Math.abs(deltaLb);
  const pct = totalWeightLossPercent(gainLb, startLb);
  return `+${Math.round(gainLb)} lb · ${pct.toFixed(1)}%`;
}

/**
 * Builds initials (e.g. "RM") from a HumanName's first given name and family name.
 *
 * @deprecated Import `getInitialsFromHumanName` from `@bariendo/lib/human-name`
 * directly. Kept as an alias so existing call sites keep resolving, and so this
 * list and `PersonCell` cannot drift apart.
 */
export const getInitials = getInitialsFromHumanName;

/**
 * Derives the Carrum "Procedure city" from the procedure appointment's Location participant.
 * Shared by the Carrum `procedureCity` column (see usePatientList.tsx) and the CSV export.
 * Returns '' when absent — callers rendering a table cell should apply their own placeholder.
 */
export function getProcedureCity(patient: Pick<PatientListQueryResultItem, 'procedureAppointment'>): string {
  const location = patient.procedureAppointment?.[0]?.participant?.find(
    (participant) => participant.actor?.resource?.resourceType === 'Location'
  )?.actor?.resource;

  return (location?.resourceType === 'Location' ? location.address?.city : undefined) ?? '';
}

/** Column headers for the Carrum partner-reporting CSV export, in display order. */
export const CARRUM_CSV_HEADERS = [
  'Patient',
  'Current state',
  'Employer',
  'City',
  'Procedure city',
  'Beginning weight',
  'Final weight',
  'Weight loss',
  'Account created',
  'Consult',
  'Procedure',
];

/**
 * Serializes a single Carrum patient row into CSV cells matching the Carrum table columns
 * (see usePatientList.tsx's `PATIENT_PAGE_FILTER_OPTIONS.CARRUM` columns), in the same order as
 * `CARRUM_CSV_HEADERS`. Uses the same accessors/helpers as those columns, but empty values are
 * '' rather than the table's '—'/'--' placeholders.
 */
export function carrumRowToCsvCells(patient: PatientListQueryResultItem): string[] {
  return [
    formatHumanName(patient.name?.[0]),
    getBariatricProcedureStageLabel(patient),
    getEmployer(patient),
    patient.address?.[0]?.city ?? '',
    getProcedureCity(patient),
    formatWeightLb(patient.firstWeightObservation?.[0]),
    formatWeightLb(patient.lastWeightObservation?.[0]),
    getWeightLossDisplay(patient.firstWeightObservation?.[0], patient.lastWeightObservation?.[0]),
    formatDate(patient.carrumEpisode?.[0]?.period?.start),
    formatDate(patient.consultationAppointment?.[0]?.start),
    formatDate(patient.procedureAppointment?.[0]?.start),
  ];
}

const CARRUM_EXPORT_PAGE_SIZE = 100;

/**
 * Reads the complete Carrum cohort for the CSV export, independently of the table.
 *
 * The table is fed by `useLiveGraphQL`, which pages 30 rows at a time on scroll — serializing its
 * rows produced a CSV containing only what the user had scrolled past. This pages the same query to
 * exhaustion instead.
 */
export async function fetchAllCarrumRows(medplum: MedplumClient): Promise<PatientListQueryResultItem[]> {
  const rows: PatientListQueryResultItem[] = [];
  let offset = 0;

  for (;;) {
    // Pagination is injected into the parsed document, not declared on it. The same
    // query backs the Carrum tab through useLiveGraphQL, which injects `_count` on
    // every fetch; a `_count` written into the document too would give Medplum two
    // arguments of the same name, and it rejects the whole request as an empty tab.
    const paginatedQuery = processGraphQLQuery(
      parse(CARRUM_PATIENT_LIST_QUERY),
      'PatientList',
      CARRUM_EXPORT_PAGE_SIZE,
      offset
    );
    const response = (await medplum.graphql(paginatedQuery, undefined, {
      filter: `_has:EpisodeOfCare:patient:type eq "${TRACK_CODESYSTEM}|${Track.CARRUM}"`,
    })) as { data?: { PatientList?: PatientListQueryResultItem[] } };

    const page = response?.data?.PatientList ?? [];
    rows.push(...page);

    if (page.length < CARRUM_EXPORT_PAGE_SIZE) {
      return rows;
    }
    offset += CARRUM_EXPORT_PAGE_SIZE;
  }
}
